import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  FileText, CheckCircle, XCircle, DollarSign, Building2,
  Loader2, Eye, Hash
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  registered: { label: "Registrada", bg: "bg-emerald-500/10", text: "text-emerald-400" },
  cancelled: { label: "Anulada", bg: "bg-red-500/10", text: "text-red-400" },
  draft: { label: "Borrador", bg: "bg-blue-500/10", text: "text-blue-400" },
};

const AdminInvoicesView = ({ auth }) => {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [fiscalClients, setFiscalClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("invoices");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const fetchData = async () => {
    try {
      const [statsRes, invRes, fcRes] = await Promise.all([
        axios.get(`${API}/admin/invoices/stats`, { auth: { username: auth.username, password: auth.password } }),
        axios.get(`${API}/admin/invoices`, { auth: { username: auth.username, password: auth.password } }),
        axios.get(`${API}/admin/fiscal-clients`, { auth: { username: auth.username, password: auth.password } }),
      ]);
      setStats(statsRes.data);
      setInvoices(invRes.data.invoices || []);
      setFiscalClients(fcRes.data.fiscal_clients || []);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 text-[#f59e0b] animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="admin-invoices-view"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Facturacion Electronica (e-CF)</h1>
          <p className="text-[#94a3b8] text-sm">Gestion de facturas de todos los clientes</p>
        </div>
        <span className="text-xs px-3 py-1 bg-[#f59e0b]/10 text-[#f59e0b] font-semibold">MOCK - DGMax API</span>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="glass-card p-5">
            <FileText className="w-7 h-7 text-[#f59e0b] mb-2" />
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-[#94a3b8] text-xs">Total Facturas</p>
          </div>
          <div className="glass-card p-5">
            <CheckCircle className="w-7 h-7 text-emerald-400 mb-2" />
            <p className="text-2xl font-bold text-white">{stats.registered}</p>
            <p className="text-[#94a3b8] text-xs">Registradas</p>
          </div>
          <div className="glass-card p-5">
            <XCircle className="w-7 h-7 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-white">{stats.cancelled}</p>
            <p className="text-[#94a3b8] text-xs">Anuladas</p>
          </div>
          <div className="glass-card p-5">
            <DollarSign className="w-7 h-7 text-[#3b82f6] mb-2" />
            <p className="text-2xl font-bold text-white">RD$ {(stats.total_amount || 0).toLocaleString("es-DO")}</p>
            <p className="text-[#94a3b8] text-xs">Total Facturado</p>
          </div>
          <div className="glass-card p-5">
            <Building2 className="w-7 h-7 text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-white">{stats.fiscal_clients}</p>
            <p className="text-[#94a3b8] text-xs">Clientes Fiscales</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: "invoices", label: "Facturas", count: invoices.length },
          { id: "fiscal", label: "Clientes Fiscales", count: fiscalClients.length },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2 ${
              tab === t.id ? "bg-[#f59e0b] text-black" : "bg-white/5 text-[#94a3b8] hover:text-white"
            }`}
            data-testid={`admin-inv-tab-${t.id}`}
          >
            {t.label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === t.id ? "bg-black/20 text-black" : "bg-white/10"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Invoices Table */}
      {tab === "invoices" && (
        <div className="glass-card overflow-hidden">
          {invoices.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-[#f59e0b]/20 mx-auto mb-3" />
              <p className="text-[#94a3b8]">No hay facturas emitidas aun</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-invoices-table">
                <thead>
                  <tr className="border-b border-white/5 text-[#94a3b8] text-xs">
                    <th className="text-left p-3">e-NCF</th>
                    <th className="text-left p-3">Tipo</th>
                    <th className="text-left p-3">Emisor</th>
                    <th className="text-left p-3">Comprador</th>
                    <th className="text-right p-3">Total</th>
                    <th className="text-center p-3">Estado</th>
                    <th className="text-right p-3">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, i) => {
                    const st = STATUS_COLORS[inv.status] || STATUS_COLORS.draft;
                    return (
                      <tr key={inv.id} className="border-b border-white/5 hover:bg-white/5 text-sm">
                        <td className="p-3 text-white font-mono text-xs">{inv.encf}</td>
                        <td className="p-3 text-[#94a3b8] text-xs">{inv.type_name}</td>
                        <td className="p-3">
                          <p className="text-white text-xs">{inv.fiscal_name}</p>
                          <p className="text-[#94a3b8] text-[10px]">RNC: {inv.fiscal_rnc}</p>
                        </td>
                        <td className="p-3">
                          <p className="text-white text-xs">{inv.buyer_name}</p>
                          <p className="text-[#94a3b8] text-[10px]">RNC: {inv.buyer_rnc}</p>
                        </td>
                        <td className="p-3 text-right text-white font-semibold text-xs">RD$ {inv.total?.toLocaleString("es-DO", { minimumFractionDigits: 2 })}</td>
                        <td className="p-3 text-center">
                          <span className={`text-[10px] px-2 py-0.5 font-semibold ${st.bg} ${st.text}`}>{st.label}</span>
                        </td>
                        <td className="p-3 text-right text-[#94a3b8] text-xs">
                          {inv.created_at ? new Date(inv.created_at).toLocaleDateString("es-DO", { day: "2-digit", month: "short" }) : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Fiscal Clients Table */}
      {tab === "fiscal" && (
        <div className="glass-card overflow-hidden">
          {fiscalClients.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="w-12 h-12 text-purple-400/20 mx-auto mb-3" />
              <p className="text-[#94a3b8]">No hay clientes fiscales registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-fiscal-table">
                <thead>
                  <tr className="border-b border-white/5 text-[#94a3b8] text-xs">
                    <th className="text-left p-3">RNC</th>
                    <th className="text-left p-3">Razon Social</th>
                    <th className="text-left p-3">Actividad</th>
                    <th className="text-left p-3">Certificado</th>
                    <th className="text-center p-3">DGII</th>
                    <th className="text-right p-3">Fecha Registro</th>
                  </tr>
                </thead>
                <tbody>
                  {fiscalClients.map((fc) => (
                    <tr key={fc.id} className="border-b border-white/5 hover:bg-white/5 text-sm">
                      <td className="p-3 text-white font-mono text-xs">{fc.rnc}</td>
                      <td className="p-3 text-white text-xs">{fc.business_name}</td>
                      <td className="p-3 text-[#94a3b8] text-xs">{fc.economic_activity || "-"}</td>
                      <td className="p-3">
                        <span className={`text-[10px] px-2 py-0.5 font-semibold ${
                          fc.certificate_status === "active" ? "bg-emerald-500/10 text-emerald-400" : "bg-yellow-500/10 text-yellow-400"
                        }`}>
                          {fc.certificate_status === "active" ? "Activo" : "Pendiente"}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        {fc.dgii_authorized ? (
                          <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" />
                        ) : (
                          <XCircle className="w-4 h-4 text-[#94a3b8] mx-auto" />
                        )}
                      </td>
                      <td className="p-3 text-right text-[#94a3b8] text-xs">
                        {fc.created_at ? new Date(fc.created_at).toLocaleDateString("es-DO") : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Types Breakdown */}
      {stats?.by_type?.length > 0 && tab === "invoices" && (
        <div className="glass-card p-5 mt-4">
          <h3 className="text-white font-semibold text-sm mb-3">Facturas por Tipo</h3>
          <div className="space-y-2">
            {stats.by_type.map((t) => (
              <div key={t.type} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-[#f59e0b] font-mono text-xs">{t.type}</span>
                  <span className="text-[#94a3b8]">{t.name}</span>
                </div>
                <span className="text-white font-bold">{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AdminInvoicesView;
